import {
  PerformInspectionButton,
  canPerformInspection,
} from "@/components/registrant/dashboard/perform-inspection-button";
import { Button } from "@/components/ui/button";
import { UserType } from "@/constants";
import type { RegistrantLicenseListLicenseResponseSchema } from "@/hera-api/client/types.gen";
import {
  cn,
  getLicenseActionLink,
  isCancelableForRegistrant,
  isDeregisterable,
  isPayable,
  isRejected,
  isRenewable,
} from "@/lib/utils";
import { Link } from "@tanstack/react-router";
import { DollarSign, LogOut, RefreshCw, RotateCcw, Trash2 } from "lucide-react";

/** Compact square target so the pinned Actions column stays narrow. */
export const LICENSE_ROW_ACTION_BUTTON_CLASS = "size-6 p-0";

const ACTION_BUTTON_PX = 24;
const ACTION_GAP_PX = 2;
/** Cell padding (px-2) plus the column's left border. */
const ACTION_CELL_CHROME_PX = 17;
/** Enough for the "Actions" header label when rows carry few icons. */
const MIN_ACTIONS_COLUMN_WIDTH = 64;

type LicenseRowActionKey =
  | "inspect"
  | "renew"
  | "reapply"
  | "pay"
  | "deregister"
  | "cancel";

/** Single source of truth for which icons a row shows, so the pinned column
 * can be sized to the widest row instead of the theoretical maximum. */
export function licenseRowActionKeys(
  license: RegistrantLicenseListLicenseResponseSchema,
  userType?: UserType,
): LicenseRowActionKey[] {
  if (userType === UserType.OMT_AMS) {
    return [];
  }
  const rejected = isRejected(license);
  const keys: LicenseRowActionKey[] = [];
  if (canPerformInspection(license)) keys.push("inspect");
  if (isRenewable(license) && !rejected) keys.push("renew");
  if (rejected) keys.push("reapply");
  // Shown without the pay grant too: those users reach the same screen but can
  // only defer with Pay Later.
  if (isPayable(license)) keys.push("pay");
  if (isDeregisterable(license)) keys.push("deregister");
  if (isCancelableForRegistrant(license.status)) keys.push("cancel");
  return keys;
}

/** Width (px) of the pinned Actions column for the given rows. */
export function licenseRowActionsColumnWidth(
  licenses: readonly RegistrantLicenseListLicenseResponseSchema[],
  userType?: UserType,
): number {
  const widest = licenses.reduce(
    (max, license) =>
      Math.max(max, licenseRowActionKeys(license, userType).length),
    0,
  );
  if (widest === 0) return MIN_ACTIONS_COLUMN_WIDTH;
  return Math.max(
    MIN_ACTIONS_COLUMN_WIDTH,
    widest * ACTION_BUTTON_PX +
      (widest - 1) * ACTION_GAP_PX +
      ACTION_CELL_CHROME_PX,
  );
}

type LicenseRowActionsProps = {
  license: RegistrantLicenseListLicenseResponseSchema;
  userType: UserType;
  canMakePayments: boolean;
  onPayClick: (licenseId: string) => void;
};

/**
 * Icon-only license row actions. Each button carries a matching `title` and
 * `aria-label` so the meaning is available on hover and to screen readers.
 */
export function LicenseRowActions({
  license,
  userType,
  canMakePayments,
  onPayClick,
}: LicenseRowActionsProps) {
  const keys = licenseRowActionKeys(license, userType);
  const payLabel = canMakePayments ? "Pay Now" : "Pay Later";

  return (
    <div className="flex flex-nowrap items-center justify-end gap-0.5">
      {keys.includes("inspect") && (
        <PerformInspectionButton licenseId={license.id} iconOnly />
      )}
      {keys.includes("renew") && (
        <Button
          variant="outline"
          size="icon"
          className={LICENSE_ROW_ACTION_BUTTON_CLASS}
          title="Renew"
          aria-label="Renew"
          asChild
        >
          <Link to={getLicenseActionLink(userType, "renew", license.id)}>
            <RefreshCw className="h-4 w-4" />
          </Link>
        </Button>
      )}
      {keys.includes("reapply") && (
        <Button
          variant="outline"
          size="icon"
          className={LICENSE_ROW_ACTION_BUTTON_CLASS}
          title="Reapply"
          aria-label="Reapply"
          asChild
        >
          <Link to={getLicenseActionLink(userType, "renew", license.id)}>
            <RotateCcw className="h-4 w-4" />
          </Link>
        </Button>
      )}
      {keys.includes("pay") && (
        <Button
          type="button"
          variant="outline"
          size="icon"
          className={LICENSE_ROW_ACTION_BUTTON_CLASS}
          title={payLabel}
          aria-label={payLabel}
          onClick={() => onPayClick(license.id)}
        >
          <DollarSign className="h-4 w-4" />
        </Button>
      )}
      {keys.includes("deregister") && (
        <Button
          variant="outline"
          size="icon"
          className={LICENSE_ROW_ACTION_BUTTON_CLASS}
          title="De-register"
          aria-label="De-register"
          asChild
        >
          <Link to={getLicenseActionLink(userType, "deregister", license.id)}>
            <LogOut className="h-4 w-4" />
          </Link>
        </Button>
      )}
      {keys.includes("cancel") && (
        <Button
          variant="outline"
          size="icon"
          className={cn(
            LICENSE_ROW_ACTION_BUTTON_CLASS,
            "text-destructive hover:bg-destructive hover:text-destructive-foreground",
          )}
          title="Cancel"
          aria-label="Cancel"
          asChild
        >
          <Link to={getLicenseActionLink(userType, "cancel", license.id)}>
            <Trash2 className="h-4 w-4" />
          </Link>
        </Button>
      )}
    </div>
  );
}
